import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { useAuth } from "../../contexts/AuthContext";
import Logo from "./Logo";

const REMEMBER_EMAIL_KEY = "df_remember_email";

function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function LoginScreen() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  // On mount, load remembered email if exists
  useEffect(() => {
    const rememberedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Handle remember me changes
  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
    if (!checked) {
      localStorage.removeItem(REMEMBER_EMAIL_KEY);
    }
  };

  // Password validation for signup
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push("Minimum 8 characters required");
    if (!/[A-Z]/.test(pwd)) errors.push("One uppercase letter required");
    if (!/[0-9]/.test(pwd)) errors.push("One numeric value required");
    if (!/[!@#$%^&*()_+\-=\[\]{}|;':",./<>?`~\\]/.test(pwd)) errors.push("One special character required");
    return errors;
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    if (mode === "register") {
      setPasswordErrors(validatePassword(val));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const trimmedEmail = email.trim();
      
      if (mode === "register") {
        await signUp(trimmedEmail, password);
      } else {
        await signIn(trimmedEmail, password);
      }
      
      // Save email if remember me is checked
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, trimmedEmail);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setError("");
    setPasswordErrors([]);
  };

  const canSubmit = email.trim().length > 3 && password.length > 0 && !loading && 
    (mode === "login" || passwordErrors.length === 0);

  return (
    <div
      className="min-h-screen password-bg flex items-center justify-center p-4"
      data-testid="login-screen"
    >
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white border border-slate-200 p-8 shadow-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <Logo size={56} />
            </div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-2">
              ENGINEERING HUB
            </h1>
            <h2 className="font-cabinet text-lg font-bold tracking-tight text-slate-700">
              Project Management Platform
            </h2>
            <p className="text-sm text-slate-500 mt-2 font-sans">
              {mode === "login"
                ? "Sign in to access your projects"
                : "Create your account to get started"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="relative">
              <EnvelopeSimple
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 rounded-none border-slate-300 bg-slate-50 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary font-mono text-sm"
                data-testid="login-email-input"
                autoFocus
                required
              />
            </div>

            <div className="relative">
              <Lock
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={handlePasswordChange}
                className="pl-10 h-12 rounded-none border-slate-300 bg-slate-50 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary font-mono text-sm"
                data-testid="login-password-input"
                required
              />
            </div>

            {mode === "login" && (
              <div className="text-right -mt-1">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-slate-500 hover:text-primary font-medium transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            )}
            {mode === "register" && password.length > 0 && (
                <ul className="text-xs mt-1.5 ml-1 space-y-0.5">
                  <li className={password.length >= 8 ? "text-green-600" : "text-red-500"}>
                    {password.length >= 8 ? "✓" : "✗"} Minimum 8 characters
                  </li>
                  <li className={/[A-Z]/.test(password) ? "text-green-600" : "text-red-500"}>
                    {/[A-Z]/.test(password) ? "✓" : "✗"} One uppercase letter
                  </li>
                  <li className={/[0-9]/.test(password) ? "text-green-600" : "text-red-500"}>
                    {/[0-9]/.test(password) ? "✓" : "✗"} One numeric value
                  </li>
                  <li className={/[!@#$%^&*()_+\-=\[\]{}|;':",./<>?`~\\]/.test(password) ? "text-green-600" : "text-red-500"}>
                    {/[!@#$%^&*()_+\-=\[\]{}|;':",./<>?`~\\]/.test(password) ? "✓" : "✗"} One special character
                  </li>
                </ul>
            )}

            {/* Remember Me Checkbox */}
            <div className="flex items-center space-x-2 pt-1">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={handleRememberMeChange}
                data-testid="remember-me-checkbox"
              />
              <Label
                htmlFor="remember-me"
                className="text-sm text-slate-600 cursor-pointer font-medium"
              >
                Remember me
              </Label>
            </div>

            {error && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-medium"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-12 rounded-sm bg-primary hover:bg-primary-hover text-white font-semibold tracking-wide uppercase text-xs"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="spinner" />
              ) : (
                <>
                  {mode === "login" ? "Sign In" : "Create Account"}
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={toggleMode}
              className="w-full text-sm text-slate-600 hover:text-slate-900 text-center font-medium transition-colors"
              data-testid="auth-mode-toggle"
            >
              {mode === "login" ? (
                <>
                  Don&apos;t have an account?{" "}
                  <span className="text-primary font-semibold">Sign up</span>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <span className="text-primary font-semibold">Sign in</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Legal notice */}
        <p className="text-xs text-slate-500 text-center mt-4">
          Your projects, evaluations, and automations are private to your email.
        </p>
      </div>
    </div>
  );
}
